import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useParams, useSearchParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import {
  useChats,
  useChatContainerRequirement,
  useChatMessages,
  useCreateChat,
  useChatStream,
  useContextSources,
  useChatCommands,
  useExecuteChatCommand,
  useAvailableWorkflowsForChat,
  useTriggerWorkflowFromChat,
  useRefreshVariableGroupTriggerOptions,
  useWorkflowTriggerSchema,
  useDebounce,
  useSkills,
  useChatAgentSelection,
} from '@/hooks'
import { useChatAttachments, useUploadChatAttachments, useDeleteChatAttachment } from '@/hooks/useChatAttachments'
import { useTenantKey } from '@/hooks/useTenantKey'
import { useConnectorReconnectRetry } from '@/hooks/useConnectorReconnectRetry'
import type { ChatAttachment, MessageAttachment } from '@/types/chat'
import { ContextSourceKinds } from '@/lib/contextSources'
import { extractValidationMessage, hasValidationError } from '@/lib/validationErrors'
import { getErrorMessage } from '@/api/errors'
import { MISSING_AGENT_CREDENTIAL_MESSAGE } from '@/lib/agentCredentialMessages'
import { ChatListSidebar, ChatArea, type ChatInputHandle } from '@/components/projects/chat'
import { readChatDraft, writeChatDraft } from '@/lib/chatDraft'
import { ProjectSectionHeader } from '@/components/projects'
import { Button } from '@/components/ui/button'
import { WorkItemStatuses } from '@/types'
import type { ChatCommandDescriptor, WorkflowDefinitionSummary, Skill } from '@/types'
import { ChatRunSidebar } from '@/components/projects/chat/ChatRunSidebar'
import { useChatRunStream } from '@/hooks/useChatRunStream'
import { readContainerPreference } from '@/lib/chatPreferences'

const forgetPendingAttachments = (chatId: string | null) => {
  if (!chatId) return
  try { sessionStorage.removeItem(`chat-pending-attach-${chatId}`) } catch {  }
}

const withoutLocalUrls = (attachments: MessageAttachment[]): ChatAttachment[] =>
  attachments.map((a) => ({
    id: a.id,
    fileName: a.fileName,
    contentType: a.contentType,
    sizeBytes: a.sizeBytes,
  }))

export function ChatPage() {
  const { projectId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeChatId, setActiveChatId] = useState<string | null>(() => searchParams.get('chatId'))
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebounce(searchTerm)

  const { data: contextReposData } = useContextSources(projectId!, undefined, { kind: ContextSourceKinds.Repository })
  const contextRepos = contextReposData?.items ?? []

  const { data: chats = [] } = useChats(projectId!, debouncedSearch || undefined)
  const { data: allChats = [] } = useChats(projectId!)
  const activeChat = allChats.find((c) => c.id === activeChatId)
  const agent = useChatAgentSelection(projectId!, activeChatId, activeChat)
  const { data: messages = [], isLoading: messagesLoading } = useChatMessages(projectId!, activeChatId)
  const createChat = useCreateChat(projectId!)
  const { sendMessage, cancelStream, isStreaming, isCancelling, streamingLogs, containerStatus, usesContainer, pendingUserMessage, sendError, dismissSendError, clearUnsentMessage, startedAt: streamStartedAt, effort: streamEffort } =
    useChatStream(projectId!, activeChatId, { activeChatEffort: activeChat?.effort ?? null })
  const composerRef = useRef<ChatInputHandle>(null)
  const activeChatIdRef = useRef(activeChatId)
  useEffect(() => {
    activeChatIdRef.current = activeChatId
  }, [activeChatId])

  const { data: commands = [] } = useChatCommands(projectId!)
  const { data: workflows = [] } = useAvailableWorkflowsForChat(projectId!)
  const { data: skills = [], isLoading: skillsLoading, isError: skillsError, error: skillsErrorDetail } = useSkills(projectId!)

  useEffect(() => {
    if (skillsError) toast.error(getErrorMessage(skillsErrorDetail) ?? 'Failed to load skills.')
  }, [skillsError, skillsErrorDetail])

  const slashCommands = useMemo(
    () =>
      commands.map((command) => {
        if (command.name !== 'update-work-item') return command
        return {
          ...command,
          parameters: command.parameters.map((param) =>
            param.name === 'status' ? { ...param, options: Object.values(WorkItemStatuses) } : param,
          ),
        }
      }),
    [commands],
  )

  const executeCommand = useExecuteChatCommand(projectId!)
  const triggerWorkflow = useTriggerWorkflowFromChat(projectId!)
  const refreshVariableGroupOptions = useRefreshVariableGroupTriggerOptions(projectId)

  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([])
  const selectedSkills = useMemo(
    () => selectedSkillIds.map((id) => skills.find((s) => s.id === id)).filter((s): s is Skill => s != null),
    [selectedSkillIds, skills],
  )

  useEffect(() => {
    if (skillsLoading || skillsError) return
    setSelectedSkillIds((prev) => {
      const validIds = new Set(skills.map((s) => s.id))
      const pruned = prev.filter((id) => validIds.has(id))
      return pruned.length === prev.length ? prev : pruned
    })
  }, [skills, skillsLoading, skillsError])
  const [activeCommand, setActiveCommand] = useState<ChatCommandDescriptor | null>(null)
  const [commandPrefill, setCommandPrefill] = useState<Record<string, string> | null>(null)
  const [activeWorkflow, setActiveWorkflow] = useState<WorkflowDefinitionSummary | null>(null)
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([])
  const [attachmentPreviews, setAttachmentPreviews] = useState<Record<string, string>>({})
  const [pendingUploadCount, setPendingUploadCount] = useState(0)
  const attachmentPreviewsRef = useRef<Record<string, string>>({})
  const localImageUrlsRef = useRef<Record<string, string>>({})
  const sentImageUrlsRef = useRef<Record<string, string>>({})
  const attachmentGenerationRef = useRef(0)
  const uploadBatchesRef = useRef<Map<AbortController, number>>(new Map())
  const [containerByChat, setContainerByChat] = useState<Record<string, boolean>>({})
  const isContainerMode = activeChatId ? (containerByChat[activeChatId] ?? true) : true

  useEffect(() => {
    if (activeChatId) {
      setContainerByChat((prev) =>
        activeChatId in prev ? prev : { ...prev, [activeChatId]: readContainerPreference(activeChatId, true) },
      )
    }
  }, [activeChatId])
  const [pendingUserMessageAttachments, setPendingUserMessageAttachments] = useState<MessageAttachment[]>([])
  const [messageAttachmentsCache, setMessageAttachmentsCache] = useState<Map<string, MessageAttachment[]>>(new Map())
  const pendingSendAttachmentsRef = useRef<{ attachments: MessageAttachment[]; prevUserCount: number } | null>(null)
  const [isSidebarOpen, setSidebarOpen] = useState(true)
  const runStream = useChatRunStream(projectId!, activeChatId)

  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  const { data: sentAttachments } = useChatAttachments(projectId!, activeChatId)
  const { data: containerRequirement, isLoading: containerRequirementLoading } =
    useChatContainerRequirement(projectId!, activeChatId)
  const containerLockedByAttachments = containerRequirementLoading || (containerRequirement?.requiresContainer ?? false)
  const uploadAttachments = useUploadChatAttachments(projectId!, activeChatId)
  const deleteAttachment = useDeleteChatAttachment(projectId!, activeChatId)

  const {
    data: triggerSchema,
    isLoading: schemaLoading,
    isError: schemaError,
    refetch: refetchTriggerSchema,
  } = useWorkflowTriggerSchema(
    projectId!,
    activeWorkflow?.name ?? null,
  )

  const schemaErrorMessage = schemaError ? 'Failed to load trigger schema. Please retry.' : null

  useEffect(() => {
    const html = document.documentElement
    const prevOverflowX = html.style.overflowX
    const prevOverflowY = html.style.overflowY

    window.scrollTo(0, 0)
    html.style.overflowX = 'auto'
    html.style.overflowY = 'hidden'

    return () => {
      html.style.overflowX = prevOverflowX
      html.style.overflowY = prevOverflowY
    }
  }, [])

  useEffect(() => {
    attachmentPreviewsRef.current = attachmentPreviews
  }, [attachmentPreviews])

  useEffect(() => () => {
    attachmentGenerationRef.current += 1
    uploadBatchesRef.current.forEach((_, controller) => controller.abort())
    uploadBatchesRef.current.clear()
    Object.values(attachmentPreviewsRef.current).forEach((url) => URL.revokeObjectURL(url))
    Object.values(localImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url))
    Object.values(sentImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url))
  }, [])

  useEffect(() => {
    setActiveCommand(null)
    setCommandPrefill(null)
    setActiveWorkflow(null)
    setSidebarOpen(true)
    setSelectedSkillIds([])
    setPendingAttachments([])
    attachmentGenerationRef.current += 1
    uploadBatchesRef.current.forEach((_, controller) => controller.abort())
    uploadBatchesRef.current.clear()
    setPendingUploadCount(0)
    Object.values(localImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url))
    localImageUrlsRef.current = {}
    Object.values(sentImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url))
    sentImageUrlsRef.current = {}
    setAttachmentPreviews({})
    if (activeChatId) {
      try {
        const stored = sessionStorage.getItem(`chat-attach-${activeChatId}`)
        setMessageAttachmentsCache(stored
          ? new Map(Object.entries(JSON.parse(stored) as Record<string, ChatAttachment[]>))
          : new Map())
      } catch {
        setMessageAttachmentsCache(new Map())
      }
      try {
        const pendingStored = sessionStorage.getItem(`chat-pending-attach-${activeChatId}`)
        if (pendingStored) {
          const pending = JSON.parse(pendingStored) as { attachments: ChatAttachment[]; prevUserCount: number }
          pendingSendAttachmentsRef.current = pending
          setPendingUserMessageAttachments(pending.attachments)
        } else {
          pendingSendAttachmentsRef.current = null
          setPendingUserMessageAttachments([])
        }
      } catch {
        pendingSendAttachmentsRef.current = null
        setPendingUserMessageAttachments([])
      }
    } else {
      setMessageAttachmentsCache(new Map())
      pendingSendAttachmentsRef.current = null
      setPendingUserMessageAttachments([])
    }
  }, [activeChatId])

  useEffect(() => {
    if (!isStreaming) {
      setPendingUserMessageAttachments([])
    }
  }, [isStreaming])

  useEffect(() => {
    if (!pendingSendAttachmentsRef.current) return
    const { attachments, prevUserCount } = pendingSendAttachmentsRef.current
    const userMessages = messages.filter((m) => m.role === 'User')
    if (userMessages.length <= prevUserCount) return
    const newMessage = userMessages[prevUserCount]
    if (!newMessage) return
    pendingSendAttachmentsRef.current = null
    forgetPendingAttachments(activeChatId)
    setMessageAttachmentsCache((prev) => {
      if (prev.has(newMessage.id)) return prev
      return new Map(prev).set(newMessage.id, attachments)
    })
    setPendingUserMessageAttachments([])
    queryClient.invalidateQueries({
      queryKey: tenantKey('projects', projectId!, 'chats', newMessage.chatId, 'attachments'),
    })
    queryClient.invalidateQueries({
      queryKey: tenantKey('projects', projectId!, 'chats', newMessage.chatId, 'container-requirement'),
    })
  }, [messages, activeChatId, projectId, queryClient, tenantKey])

  useEffect(() => {
    if (!activeChatId || messageAttachmentsCache.size === 0) return
    try {
      sessionStorage.setItem(
        `chat-attach-${activeChatId}`,
        JSON.stringify(
          Object.fromEntries(
            [...messageAttachmentsCache.entries()].map(([id, group]) => [id, withoutLocalUrls(group)]),
          ),
        ),
      )
    } catch {
    }
  }, [messageAttachmentsCache, activeChatId])

  useEffect(() => {
    const current = searchParams.get('chatId')
    if (activeChatId && current !== activeChatId) {
      const next = new URLSearchParams(searchParams)
      next.set('chatId', activeChatId)
      setSearchParams(next, { replace: true })
    } else if (!activeChatId && current) {
      const next = new URLSearchParams(searchParams)
      next.delete('chatId')
      setSearchParams(next, { replace: true })
    }
  }, [activeChatId, searchParams, setSearchParams])

  const handleCreateChat = async () => {
    const result = await createChat.mutateAsync()
    setActiveChatId(result.chatId)
  }

  const handleDeleteChat = (chatId: string) => {
    if (activeChatId === chatId) {
      setActiveChatId(null)
    }
    try { localStorage.removeItem(`chat-container-${chatId}`) } catch {}
    writeChatDraft(chatId, '')
  }

  const handleSendMessage = (message: string, useContainer: boolean): boolean => {
    if (agent.isLoading) return false
    if (!agent.isError && agent.providers.length === 0) {
      toast.error(MISSING_AGENT_CREDENTIAL_MESSAGE)
      return false
    }
    const agentSelection = agent.selectionToSend
    const attachmentIds = pendingAttachments.map((a) => a.id)
    const unsentAttachments = pendingAttachments
    const unsentImageUrls = { ...localImageUrlsRef.current }
    const unsentPreviews = attachmentPreviews
    const sentChatId = activeChatId
    if (pendingAttachments.length > 0) {
      const sent: MessageAttachment[] = pendingAttachments.map((a) => ({
        ...a,
        url: localImageUrlsRef.current[a.id],
      }))
      const pendingState = {
        attachments: sent,
        prevUserCount: messages.filter((m) => m.role === 'User').length,
      }
      setPendingUserMessageAttachments(sent)
      pendingSendAttachmentsRef.current = pendingState
      if (activeChatId) {
        try {
          sessionStorage.setItem(
            `chat-pending-attach-${activeChatId}`,
            JSON.stringify({ ...pendingState, attachments: withoutLocalUrls(sent) }),
          )
        } catch {  }
      }
    }
    setPendingAttachments([])
    // Revoking the current batch here would blank the image the user just sent; only the previous
    // send's blobs are released, which bounds retention when linking never happens.
    Object.values(sentImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url))
    sentImageUrlsRef.current = { ...localImageUrlsRef.current }
    localImageUrlsRef.current = {}
    setAttachmentPreviews({})
    const skillIds = selectedSkills.map((s) => s.id)
    setSelectedSkillIds([])
    void sendMessage(
      message,
      attachmentIds.length > 0 ? attachmentIds : undefined,
      useContainer,
      agentSelection?.model ?? undefined,
      agentSelection?.effort ?? undefined,
      skillIds.length > 0 ? skillIds : undefined,
      agentSelection?.agentProvider,
    ).then(({ sent, messageCreated }) => {
      if (sent || messageCreated) return
      if (activeChatIdRef.current !== sentChatId) {
        forgetPendingAttachments(sentChatId)
        if (sentChatId && !readChatDraft(sentChatId)) {
          writeChatDraft(sentChatId, message)
          clearUnsentMessage(sentChatId)
        }
        return
      }
      setSelectedSkillIds((prev) => (prev.length === 0 ? skillIds : prev))
      restoreUnsentAttachments(unsentAttachments, unsentImageUrls, unsentPreviews)
      if (sentChatId && composerRef.current?.restoreDraft(message)) {
        clearUnsentMessage(sentChatId)
      }
    })
    return true
  }

  // A send that never reached the server leaves no user message to attach to, so the armed
  // pending state would latch onto whichever message lands next.
  const restoreUnsentAttachments = (
    attachments: ChatAttachment[],
    imageUrls: Record<string, string>,
    previews: Record<string, string>,
  ) => {
    pendingSendAttachmentsRef.current = null
    forgetPendingAttachments(activeChatId)
    setPendingUserMessageAttachments([])

    if (attachments.length === 0) return

    Object.entries(imageUrls).forEach(([id, url]) => {
      if (sentImageUrlsRef.current[id] === url) delete sentImageUrlsRef.current[id]
    })
    localImageUrlsRef.current = { ...localImageUrlsRef.current, ...imageUrls }

    setPendingAttachments((prev) => {
      const known = new Set(prev.map((a) => a.id))
      return [...prev, ...attachments.filter((a) => !known.has(a.id))]
    })
    setAttachmentPreviews((prev) => ({ ...prev, ...previews }))
  }

  const handleSelectSkill = (skill: Skill) => {
    setSelectedSkillIds((prev) => (prev.includes(skill.id) ? prev : [...prev, skill.id]))
  }

  const handleRemoveSkill = (skillId: string) => {
    setSelectedSkillIds((prev) => prev.filter((id) => id !== skillId))
  }

  const handleClearSkills = () => {
    setSelectedSkillIds([])
  }

  useEffect(() => {
    if (!sentAttachments?.length) return

    const remaining = { ...sentImageUrlsRef.current }
    let released = false

    for (const attachment of sentAttachments) {
      const localUrl = remaining[attachment.id]
      if (!localUrl) continue
      URL.revokeObjectURL(localUrl)
      delete remaining[attachment.id]
      released = true
    }

    if (released) sentImageUrlsRef.current = remaining
  }, [sentAttachments])

  const handleAttachmentLoadError = () => {
    if (!activeChatId) return
    queryClient.invalidateQueries({
      queryKey: tenantKey('projects', projectId!, 'chats', activeChatId, 'attachments'),
    })
  }

  const attachmentsByMessageId = useMemo(() => {
    const merged = new Map<string, MessageAttachment[]>(messageAttachmentsCache)
    const persisted = new Map<string, MessageAttachment[]>()

    for (const attachment of sentAttachments ?? []) {
      const group = persisted.get(attachment.messageId) ?? []
      group.push(attachment)
      persisted.set(attachment.messageId, group)
    }

    persisted.forEach((group, messageId) => merged.set(messageId, group))

    return merged
  }, [messageAttachmentsCache, sentAttachments])

  const handleConnectorReconnected = useConnectorReconnectRetry({
    projectId: projectId!,
    chatId: activeChatId,
    messages,
    attachmentsByMessageId,
    onResend: (message, attachmentIds) =>
      sendMessage(
        message,
        attachmentIds,
        isContainerMode || agent.forcesContainer,
        agent.selectionToSend?.model ?? undefined,
        agent.selectionToSend?.effort ?? undefined,
        undefined,
        agent.selectionToSend?.agentProvider,
      ),
  })

  const handleAttachFiles = async (files: File[], withPreviews = false) => {
    if (!activeChatId) return
    const generation = attachmentGenerationRef.current
    const abortController = new AbortController()
    uploadBatchesRef.current.set(abortController, files.length)
    setPendingUploadCount((prev) => prev + files.length)
    try {
      const result = await uploadAttachments.mutateAsync({ files, signal: abortController.signal })
      const newAttachments: ChatAttachment[] = result.attachments.map((att, i) => ({
        id: att.id,
        fileName: att.fileName,
        contentType: files[i].type || 'application/octet-stream',
        sizeBytes: files[i].size,
      }))
      const imageUrls = Object.fromEntries(
        newAttachments
          .map((att, i) => [att.id, files[i]] as const)
          .filter(([, file]) => file?.type.startsWith('image/'))
          .map(([id, file]) => [id, URL.createObjectURL(file)]),
      )

      if (generation !== attachmentGenerationRef.current) {
        Object.values(imageUrls).forEach((url) => URL.revokeObjectURL(url))
        newAttachments.forEach((attachment) => {
          void deleteAttachment.mutateAsync(attachment.id).catch(() => {})
        })
        return
      }

      localImageUrlsRef.current = { ...localImageUrlsRef.current, ...imageUrls }

      setPendingAttachments((prev) => [...prev, ...newAttachments])
      if (withPreviews && Object.keys(imageUrls).length > 0) {
        setAttachmentPreviews((prev) => ({ ...prev, ...imageUrls }))
      }
    } catch {
      if (generation === attachmentGenerationRef.current && !abortController.signal.aborted) {
        toast.error('Failed to upload attachments.')
      }
    } finally {
      const stillTracked = uploadBatchesRef.current.delete(abortController)
      if (stillTracked && generation === attachmentGenerationRef.current) {
        setPendingUploadCount((prev) => Math.max(0, prev - files.length))
      }
    }
  }

  const handleCancelUpload = () => {
    uploadBatchesRef.current.forEach((_, controller) => controller.abort())
    uploadBatchesRef.current.clear()
    setPendingUploadCount(0)
  }

  const handleRemoveAttachment = async (id: string) => {
    try {
      await deleteAttachment.mutateAsync(id)
      setPendingAttachments((prev) => prev.filter((a) => a.id !== id))
      const removedUrl = localImageUrlsRef.current[id]
      if (removedUrl) {
        URL.revokeObjectURL(removedUrl)
        localImageUrlsRef.current = Object.fromEntries(
          Object.entries(localImageUrlsRef.current).filter(([key]) => key !== id),
        )
      }
      setAttachmentPreviews((prev) =>
        prev[id] ? Object.fromEntries(Object.entries(prev).filter(([key]) => key !== id)) : prev,
      )
    } catch {
      toast.error('Failed to remove attachment.')
    }
  }

  const handleCommandSelect = (command: ChatCommandDescriptor) => {
    if (!activeChatId) {
      toast.error('Select a chat first.')
      return
    }
    setActiveCommand(command)
    setCommandPrefill(null)
    setActiveWorkflow(null)
    handleClearSkills()
  }

  const handleCommandCancel = () => {
    setActiveCommand(null)
    setCommandPrefill(null)
  }

  const handleCommandExecute = async (params: Record<string, string>) => {
    if (!activeCommand) return
    try {
      const result = await executeCommand.mutateAsync({
        chatId: activeChatId!,
        commandName: activeCommand.name,
        params,
      })
      if (!result.success) {
        toast.error(result.errorMessage ?? 'Failed to execute command')
      }
      setActiveCommand(null)
      setCommandPrefill(null)
    } catch {
      toast.error('Failed to execute command')
    }
  }

  const handleCommandRunAgain = (commandName: string, params: Record<string, string>) => {
    const cmd = slashCommands.find((c) => c.name === commandName || c.displayName === commandName)
    if (cmd) {
      setActiveCommand(cmd)
      setCommandPrefill(params)
    }
  }

  const handleWorkflowSelect = (workflow: WorkflowDefinitionSummary) => {
    if (!activeChatId) {
      toast.error('Select a chat first.')
      return
    }
    setActiveWorkflow(workflow)
    setActiveCommand(null)
    handleClearSkills()
  }

  const handleWorkflowCancel = () => {
    setActiveWorkflow(null)
  }


  const handleContainerModeChange = (value: boolean) => {
    if (!activeChatId) return
    setContainerByChat((prev) => ({ ...prev, [activeChatId]: value }))
    try { localStorage.setItem(`chat-container-${activeChatId}`, String(value)) } catch {}
  }

  const handleWorkflowTrigger = async (inputValuesJson: string, variableGroupIds: string[]) => {
    if (!activeWorkflow) return
    if (!agent.isLoading && agent.providers.length === 0) {
      toast.error(MISSING_AGENT_CREDENTIAL_MESSAGE)
      return
    }
    try {
      await triggerWorkflow.mutateAsync({
        workflowName: activeWorkflow.name,
        chatId: activeChatId!,
        inputValuesJson,
        variableGroupIds,
      })
      setActiveWorkflow(null)
    } catch (err) {
      if (hasValidationError(err, 'VariableGroups')) void refreshVariableGroupOptions()
      toast.error(extractValidationMessage(err) ?? 'Failed to trigger workflow')
    }
  }

  return (
    <div className="flex h-[calc(100vh-1rem)] min-h-0 flex-col gap-6">
      <div className="shrink-0 space-y-6">
        <ProjectSectionHeader sectionName="Chats">
          <Button
            onClick={handleCreateChat}
            disabled={createChat.isPending}
          >
            <Plus className="h-4 w-4" />
            New Chat
          </Button>
        </ProjectSectionHeader>
      </div>
      <div className="flex flex-1 min-h-0 gap-3 pb-8 overflow-hidden">
        <div className="shrink-0 grow-0 basis-1/5 min-w-56 max-w-80 min-h-0 overflow-hidden">
          <ChatListSidebar
            projectId={projectId!}
            chats={chats}
            activeChatId={activeChatId}
            onSelectChat={setActiveChatId}
            onCreateChat={handleCreateChat}
            onDeleteChat={handleDeleteChat}
            isCreating={createChat.isPending}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </div>
        <div className="flex-1 min-w-0 min-h-0 bg-panel">
          <ChatArea
            chatId={activeChatId}
            chatTitle={chats.find((c) => c.id === activeChatId)?.title ?? null}
            projectId={projectId!}
            messages={messages}
            isLoading={messagesLoading}
            isStreaming={isStreaming}
            isCancelling={isCancelling}
            streamingLogs={streamingLogs}
            containerStatus={containerStatus}
            usesContainer={usesContainer}
            streamStartedAt={streamStartedAt}
            streamEffort={streamEffort}
            pendingUserMessage={pendingUserMessage}
            sendError={sendError}
            onDismissSendError={dismissSendError}
            composerRef={composerRef}
            isContainerMode={isContainerMode}
            containerLockedByAttachments={containerLockedByAttachments}
            onContainerModeChange={handleContainerModeChange}
            onSendMessage={handleSendMessage}
            onConnectorReconnected={handleConnectorReconnected}
            onCancelStream={cancelStream}
            contextRepos={contextRepos}
            commands={slashCommands}
            workflows={workflows}
            skills={skills}
            skillsLoading={skillsLoading}
            selectedSkills={selectedSkills}
            onSelectSkill={handleSelectSkill}
            onRemoveSkill={handleRemoveSkill}
            onClearSkills={handleClearSkills}
            activeCommand={activeCommand}
            commandPrefill={commandPrefill}
            activeWorkflow={activeWorkflow}
            triggerSchema={triggerSchema ?? null}
            schemaLoading={schemaLoading}
            schemaErrorMessage={schemaErrorMessage}
            onCommandSelect={handleCommandSelect}
            onCommandCancel={handleCommandCancel}
            onCommandExecute={handleCommandExecute}
            isExecutingCommand={executeCommand.isPending}
            onCommandRunAgain={handleCommandRunAgain}
            onWorkflowSelect={handleWorkflowSelect}
            onWorkflowCancel={handleWorkflowCancel}
            onWorkflowTrigger={handleWorkflowTrigger}
            onWorkflowRetrySchema={() => {
              void refetchTriggerSchema()
            }}
            isTriggering={triggerWorkflow.isPending}
            attachments={pendingAttachments}
            attachmentPreviews={attachmentPreviews}
            pendingUploadCount={pendingUploadCount}
            onAttachFiles={handleAttachFiles}
            onCancelUpload={handleCancelUpload}
            onRemoveAttachment={handleRemoveAttachment}
            pendingUserMessageAttachments={pendingUserMessageAttachments}
            messageAttachmentsCache={attachmentsByMessageId}
            onAttachmentLoadError={handleAttachmentLoadError}
            agent={agent}
            isSidebarOpen={isSidebarOpen}
            setSidebarOpen={setSidebarOpen}
            runStream={runStream}
          />
        </div>
        {activeChatId && isSidebarOpen && (
          <ChatRunSidebar projectId={projectId!} chatId={activeChatId} runStreamState={runStream} isSidebarOpen={isSidebarOpen} />
        )}
      </div>
    </div>
  )
}
